import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LOCALITIES } from "@/components/FilterBar";
import { ImageDropzone } from "@/components/ImageDropzone";
import { OTPDialog } from "@/components/OTPDialog";
import { PricingSuggestion } from "@/components/PricingSuggestion";
import { VerificationTierPicker } from "@/components/VerificationTierPicker";
import { payWithRazorpay } from "@/lib/razorpay";
import { formatApiError, formatINR } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, CreditCard } from "lucide-react";

const LOCALITY_COORDS = {
  Jayalakshmipuram: [12.3168, 76.6278],
  Gokulam: [12.3276, 76.6231],
  Vijayanagar: [12.3382, 76.6085],
  Kuvempunagar: [12.2905, 76.6284],
  Saraswathipuram: [12.3021, 76.6358],
  Hebbal: [12.3521, 76.6098],
  Nazarbad: [12.3041, 76.6552],
};

export const PostProperty = ({ onAuth }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [images, setImages] = useState([]);
  const [phoneVerified, setPhoneVerified] = useState(!!user?.phone_verified);
  const [phone, setPhone] = useState(user?.phone || "");
  const [feeConfig, setFeeConfig] = useState({ listing_fee_paise: 39900, document_verified_fee_paise: 199900 });
  const [form, setForm] = useState({
    title: "", description: "", listing_type: "buy", property_type: "apartment",
    price: "", bhk: "2", area_sqft: "", locality: "Jayalakshmipuram", address: "",
    amenities: "", furnishing: "unfurnished", virtual_tour_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);

  useEffect(() => {
    setPhoneVerified(!!user?.phone_verified);
    setPhone(user?.phone || "");
    api.get("/payments/config").then(({ data }) => setFeeConfig(data));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-3xl text-[var(--teak)]">Sign in to post a property</h1>
        <p className="text-[var(--ink-muted)] mt-3">Create an account or sign in to list your home — it takes 2 minutes.</p>
        <button className="btn-primary mt-6" onClick={() => onAuth?.("login")} data-testid="post-signin-btn">Sign In</button>
      </div>
    );
  }

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (images.length === 0) {
      toast.error("Please upload at least one photo of the property");
      return;
    }
    // Admins post directly — no phone verification, no payment, auto-approved by backend
    if (user?.role === "admin") {
      return adminDirectPost();
    }
    if (!phoneVerified) {
      toast.error("Please verify your phone number before submitting");
      return;
    }
    // Open tier picker instead of paying immediately
    setTierOpen(true);
  };

  const adminDirectPost = async () => {
    setBusy(true);
    try {
      const [lat, lng] = LOCALITY_COORDS[form.locality] || [12.3168, 76.6412];
      const payload = {
        ...form,
        price: Number(form.price),
        bhk: Number(form.bhk),
        area_sqft: Number(form.area_sqft),
        lat, lng,
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        images,
        posted_by_role: "agent",
        agent_name: user.name,
        agent_phone: phone || "+910000000000",
        verification_tier: "document_verified",
      };
      const { data } = await api.post("/properties", payload);
      toast.success("Listing published instantly (admin)");
      nav(`/property/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  const createAfterPayment = async ({ tier, purpose }) => {
    setBusy(true);
    const pay = await payWithRazorpay(purpose, { name: user.name, email: user.email, phone });
    if (!pay.ok) {
      toast.error(pay.error === "cancelled" ? "Payment cancelled" : `Payment failed: ${pay.error || ""}`);
      setBusy(false);
      return;
    }
    const [lat, lng] = LOCALITY_COORDS[form.locality] || [12.3168, 76.6412];
    const payload = {
      ...form,
      price: Number(form.price),
      bhk: Number(form.bhk),
      area_sqft: Number(form.area_sqft),
      lat, lng,
      amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
      images,
      posted_by_role: user.role === "agent" ? "agent" : "owner",
      agent_name: user.name,
      agent_phone: phone,
      verification_tier: tier,
    };
    try {
      const { data } = await api.post("/properties", payload);
      toast.success(
        tier === "document_verified"
          ? "Payment received. Our legal team will vet your documents and publish within 24 hours."
          : "Payment received. Our team will verify location and photos within 24 hours."
      );
      setTierOpen(false);
      nav(`/property/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="mb-8">
        <span className="section-eyebrow">Peer-to-Peer or Agent</span>
        <h1 className="font-serif text-4xl text-[var(--teak)] mt-2">Post a Property</h1>
        <p className="text-[var(--ink-muted)] mt-3">Zero commission. Direct inquiries via WhatsApp. Verified-listing badge on approval.</p>
      </div>

      {/* instead of 99 make it as 399 */}
      {user?.role === "admin" ? (
        <div className="mb-6 p-4 rounded-2xl bg-[rgba(200,90,50,0.08)] border border-[rgba(200,90,50,0.25)] flex items-start gap-3" data-testid="admin-direct-notice">
          <ShieldCheck className="w-5 h-5 text-[var(--primary)] mt-0.5" />
          <div className="text-sm text-[var(--teak)] flex-1">
            <div className="font-semibold">Admin direct publish</div>
            <p className="text-[var(--ink-muted)] mt-1">
              You&apos;re posting as admin — this listing will go live immediately with no payment and no verification queue.
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 rounded-2xl bg-[rgba(30,67,56,0.06)] border border-[rgba(30,67,56,0.2)] flex items-start gap-3" data-testid="verification-notice">
          <ShieldCheck className="w-5 h-5 text-[var(--emerald)] mt-0.5" />
          <div className="text-sm text-[var(--teak)] flex-1">
            <div className="font-semibold">Human-verified listings only</div>
            <p className="text-[var(--ink-muted)] mt-1">
              After filling out your property details, you&apos;ll choose a verification plan — <strong>Location &amp; Owner (₹{(feeConfig.listing_fee_paise / 100).toLocaleString("en-IN")})</strong> or <strong>Document Verified (₹{(feeConfig.document_verified_fee_paise / 100).toLocaleString("en-IN")})</strong>. You&apos;ll get an in-app alert the moment it&apos;s live.
            </p>
          </div>
        </div>
      )}

      {/* Phone verification bar — hidden for admin direct-publish */}
      {user?.role !== "admin" && (
      <div className="mb-6 p-4 rounded-2xl bg-white border border-[var(--line)] flex items-center justify-between gap-4" data-testid="phone-verify-bar">
        <div className="text-sm">
          <div className="font-semibold text-[var(--teak)]">
            {phoneVerified ? `✓ Phone verified: ${phone}` : "Verify your phone first"}
          </div>
          <div className="text-xs text-[var(--ink-muted)] mt-0.5">
            {phoneVerified ? "Buyers can reach you via WhatsApp." : "Required before posting — buyers contact you here."}
          </div>
        </div>
        <OTPDialog
          phone={phone}
          onVerified={(p) => { setPhone(p); setPhoneVerified(true); }}
          trigger={
            <button className={phoneVerified ? "btn-ghost" : "btn-primary"} data-testid="phone-verify-btn">
              {phoneVerified ? "Change" : "Verify Phone"}
            </button>
          }
        />
      </div>
      )}

      <form onSubmit={submit} className="space-y-5 p-6 lg:p-8 rounded-2xl bg-white border border-[var(--line)]" data-testid="post-property-form">
        <div>
          <Label>Property Photos (drag & drop)</Label>
          <div className="mt-2">
            <ImageDropzone value={images} onChange={setImages} />
          </div>
        </div>
        <div>
          <Label>Title</Label>
          <Input required value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="e.g. 3BHK heritage flat with Chamundi view" data-testid="post-title" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea required rows={4} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="post-description" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Listing Type</Label>
            <Select value={form.listing_type} onValueChange={(v) => upd("listing_type", v)}>
              <SelectTrigger data-testid="post-listing-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="buy">For Sale</SelectItem>
                <SelectItem value="rent">For Rent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Property Type</Label>
            <Select value={form.property_type} onValueChange={(v) => upd("property_type", v)}>
              <SelectTrigger data-testid="post-property-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="villa">Villa</SelectItem>
                <SelectItem value="heritage">Heritage</SelectItem>
                <SelectItem value="independent_house">Independent House</SelectItem>
                <SelectItem value="plot">Plot / Site</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Price (₹)</Label>
            <Input required type="number" value={form.price} onChange={(e) => upd("price", e.target.value)} placeholder={form.listing_type === "rent" ? "e.g. 35000" : "e.g. 15000000"} data-testid="post-price" />
          </div>
          <div>
            <Label>Area (sq.ft)</Label>
            <Input required type="number" value={form.area_sqft} onChange={(e) => upd("area_sqft", e.target.value)} data-testid="post-area" />
          </div>
        </div>

        <PricingSuggestion
          locality={form.locality}
          listingType={form.listing_type}
          bhk={form.bhk}
          areaSqft={form.area_sqft}
          onApply={(v) => upd("price", String(v))}
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>BHK</Label>
            <Select value={form.bhk} onValueChange={(v) => upd("bhk", v)}>
              <SelectTrigger data-testid="post-bhk"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Plot</SelectItem>
                <SelectItem value="1">1 BHK</SelectItem>
                <SelectItem value="2">2 BHK</SelectItem>
                <SelectItem value="3">3 BHK</SelectItem>
                <SelectItem value="4">4+ BHK</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Furnishing</Label>
            <Select value={form.furnishing} onValueChange={(v) => upd("furnishing", v)}>
              <SelectTrigger data-testid="post-furnishing"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unfurnished">Unfurnished</SelectItem>
                <SelectItem value="semi-furnished">Semi-furnished</SelectItem>
                <SelectItem value="fully-furnished">Fully furnished</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Locality</Label>
            <Select value={form.locality} onValueChange={(v) => upd("locality", v)}>
              <SelectTrigger data-testid="post-locality"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Address</Label>
            <Input required value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Street / cross / landmark" data-testid="post-address" />
          </div>
        </div>
        <div>
          <Label>Amenities (comma separated)</Label>
          <Input value={form.amenities} onChange={(e) => upd("amenities", e.target.value)} placeholder="Lift, Parking, Security, Clubhouse" data-testid="post-amenities" />
        </div>
        <div>
          <Label>Virtual Tour URL (optional)</Label>
          <Input
            type="url"
            value={form.virtual_tour_url}
            onChange={(e) => upd("virtual_tour_url", e.target.value)}
            placeholder="https://matterport.com/... or https://youtu.be/..."
            data-testid="post-virtual-tour-url"
          />
          <p className="text-[11px] text-[var(--ink-muted)] mt-1">Add a 360° or video walkthrough — your listing earns a <strong>Verified Virtual</strong> badge once admin approves.</p>
        </div>
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="post-submit-btn">
          <CreditCard className="w-4 h-4" />
          {user?.role === "admin"
            ? (busy ? "Publishing…" : "Publish listing instantly")
            : "Fill details → Choose verification plan"}
        </button>
      </form>

      <VerificationTierPicker
        open={tierOpen}
        onOpenChange={setTierOpen}
        feeConfig={feeConfig}
        onPick={createAfterPayment}
        busy={busy}
      />
    </div>
  );
};
